import { useState, useEffect } from 'react';
import { notificationService, NotificationPermissionStatus, FirebaseNotificationService } from '../services/firebaseNotification';

export interface UseNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  requestPermission: () => Promise<void>;
  enableNotifications: () => Promise<void>;
  disableNotifications: () => Promise<void>;
}

export const useNotifications = (): UseNotificationsReturn => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Khởi tạo permission status
    setPermission(notificationService.getPermissionStatus());
    setToken(notificationService.getCurrentToken());
    
    // Kiểm tra support async
    const checkSupport = async () => {
      try {
        const supported = await FirebaseNotificationService.isSupported();
        setIsSupported(supported);
      } catch (err) {
        setIsSupported(false);
      }
    };
    
    checkSupport();
  }, []);

  const requestPermission = async (): Promise<void> => {
    if (!isSupported) {
      setError('Notifications are not supported in this browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result: NotificationPermissionStatus = await notificationService.initialize();
      
      setPermission(result.permission);
      
      if (result.token) {
        setToken(result.token);
        // Gửi token lên server nếu cần
        await notificationService.sendTokenToServer(result.token);
      }
      
      if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const enableNotifications = async (): Promise<void> => {
    await requestPermission();
  };

  const disableNotifications = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await notificationService.deleteToken();
      if (success) {
        setToken(null);
        setPermission('default');
      } else {
        setError('Failed to disable notifications');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isSupported,
    permission,
    token,
    isLoading,
    error,
    requestPermission,
    enableNotifications,
    disableNotifications,
  };
};
